import { ContentCopy } from '@mui/icons-material';
import { Box, Grid, IconButton, Paper, Stack, Typography } from '@mui/material';
import { t } from 'i18next';
import { PropsWithChildren } from 'react';
import { Interpolation } from '../../../config/i18n/components/Interpolation';

type Props = {
  consumerKey: string | null;
  publicKey: string | null;
};

type ItemProps = PropsWithChildren<{ isLabel: boolean; hasCopyIcon: boolean }>;

const Item = ({ children, isLabel, hasCopyIcon }: ItemProps) => {
  const gridSize = isLabel ? 3 : 9;

  const copyFunc = (event: React.SyntheticEvent) => {
    const copyText = event.currentTarget.parentElement?.textContent;
    if (copyText) {
      navigator.clipboard.writeText(copyText);
    }
  };

  const styles = {
    backgroundColor: isLabel ? 'rgb(230, 230, 230)' : '#fff',
    textAlign: 'left',
    color: 'text.primary',
    borderRadius: isLabel ? '4px 0 0 4px' : '0 4px 4px 0',
    padding: 1,
    height: '100%',
  };
  return (
    <Grid item xs={gridSize}>
      <Paper sx={styles}>
        {hasCopyIcon && (
          <Stack direction={'row'} alignItems={'start'} justifyContent={'space-between'}>
            {children}
            <IconButton onClick={copyFunc} size="small" title={t('a11y.jiraIntegrations.copyIconLabel')}>
              <ContentCopy fontSize="inherit" />
            </IconButton>
          </Stack>
        )}
        {!hasCopyIcon && children}
      </Paper>
    </Grid>
  );
};

export const JiraConfigurationForm = ({ consumerKey, publicKey }: Props) => {
  const applicationUrl = window.location.origin;

  return (
    <Box sx={{ width: 1, maxWidth: '60rem', '& li:not(:first-of-type)': { mt: 1 }, '& .MuiGrid-container': { mt: 1 } }}>
      <ol>
        <li>
          <Interpolation i18nKey="jiraIssueExport.linkInstructions.step1" />
        </li>
        <li>
          <Interpolation i18nKey="jiraIssueExport.linkInstructions.step2" />
        </li>
        <li>
          <Interpolation i18nKey="jiraIssueExport.linkInstructions.step3.instruction" />
          <Grid container>
            <Item isLabel={true} hasCopyIcon={false}>
              {t('jiraIssueExport.linkInstructions.step3.applicationUrl')}:
            </Item>
            <Item isLabel={false} hasCopyIcon={true}>
              {applicationUrl}
            </Item>
          </Grid>
          <Interpolation
            component="p"
            i18nKey="jiraIssueExport.linkInstructions.step3.secondaryInstruction"
            sx={{ py: 1, color: 'text.secondary' }}
            variant="body2"
          />
        </li>
        <li>
          <Typography>{t('jiraIssueExport.linkInstructions.step4.instruction')}:</Typography>
          <Grid container>
            <Item isLabel={true} hasCopyIcon={false}>
              {t('jiraIssueExport.linkInstructions.step4.applicationName')}:
            </Item>
            <Item isLabel={false} hasCopyIcon={true}>
              {t('jiraIssueExport.linkInstructions.step4.aqtiveGuardApplicationName')}
            </Item>
            <Item isLabel={true} hasCopyIcon={false}>
              {t('jiraIssueExport.linkInstructions.step4.applicationType')}:
            </Item>
            <Item isLabel={false} hasCopyIcon={false}>
              {t('jiraIssueExport.linkInstructions.step4.genericApplication')}
            </Item>
          </Grid>
          <Interpolation
            component="p"
            i18nKey="jiraIssueExport.linkInstructions.step4.secondaryInstruction"
            sx={{ py: 1, color: 'text.secondary' }}
            variant="body2"
          />
        </li>
        <li>
          <Interpolation i18nKey="jiraIssueExport.linkInstructions.step5.instruction" />
          <Grid container>
            <Item isLabel={true} hasCopyIcon={false}>
              {t('jiraIssueExport.linkInstructions.step5.consumerKey')}:
            </Item>
            <Item isLabel={false} hasCopyIcon={true}>
              {consumerKey}
            </Item>
            <Item isLabel={true} hasCopyIcon={false}>
              {t('jiraIssueExport.linkInstructions.step5.consumerName')}:
            </Item>
            <Item isLabel={false} hasCopyIcon={true}>
              {t('jiraIssueExport.linkInstructions.step5.aqtiveGuardConsumerName')}
            </Item>
            <Item isLabel={true} hasCopyIcon={false}>
              {t('jiraIssueExport.linkInstructions.step5.publicKey')}:
            </Item>
            <Item isLabel={false} hasCopyIcon={true}>
              <Box sx={{ overflowWrap: 'anywhere', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>{publicKey}</Box>
            </Item>
          </Grid>
          <Interpolation
            component="p"
            i18nKey="jiraIssueExport.linkInstructions.step5.secondaryInstruction"
            sx={{ py: 1, color: 'text.secondary' }}
            variant="body2"
          />
        </li>
        <li>
          <Interpolation component="p" i18nKey="jiraIssueExport.linkInstructions.step6.instruction" sx={{ py: 1 }} />
        </li>
      </ol>
    </Box>
  );
};
